import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Website/Navbar';
import Footer from '../components/Website/Footer';
import { supportAPI } from '../services/api';
import './Support.css';

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const Support: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    subject: false,
    message: false
  });
  
  // Live Chat States
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      text: 'Hello! Welcome to ZiTech Solutions Support. How can I help you today?',
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [chatStarted, setChatStarted] = useState(false);
  const [botThinking, setBotThinking] = useState(false);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Validation functions
  const validateName = (name: string): string => {
    if (!name.trim()) return 'Full name is required';
    if (name.trim().length < 2) return 'Name must be at least 2 characters';
    if (name.trim().length > 50) return 'Name must be less than 50 characters';
    return '';
  };

  const validateEmail = (email: string): string => {
    if (!email.trim()) return 'Email address is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validateSubject = (subject: string): string => {
    if (!subject) return 'Please select a subject';
    return '';
  };

  const validateMessage = (message: string): string => {
    if (!message.trim()) return 'Message is required';
    if (message.trim().length < 10) return 'Message must be at least 10 characters';
    if (message.trim().length > 2000) return 'Message must be less than 2000 characters';
    return '';
  };

  const validateField = (field: keyof FormErrors, value: string): string => {
    switch (field) {
      case 'name': return validateName(value);
      case 'email': return validateEmail(value);
      case 'subject': return validateSubject(value);
      case 'message': return validateMessage(value);
      default: return '';
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = validateField(name as keyof FormErrors, formData[name as keyof typeof formData]);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // If touched, validate on change
    if (touched[name as keyof typeof touched]) {
      const error = validateField(name as keyof FormErrors, value);
      setFormErrors(prev => ({ ...prev, [name]: error }));
    }
    
    if (error) setError('');
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;
    
    // Validate all fields
    const nameError = validateName(formData.name);
    const emailError = validateEmail(formData.email);
    const subjectError = validateSubject(formData.subject);
    const messageError = validateMessage(formData.message);
    
    if (nameError) {
      errors.name = nameError;
      isValid = false;
    }
    if (emailError) {
      errors.email = emailError;
      isValid = false;
    }
    if (subjectError) {
      errors.subject = subjectError;
      isValid = false;
    }
    if (messageError) {
      errors.message = messageError;
      isValid = false;
    }
    
    setFormErrors(errors);
    setTouched({
      name: true,
      email: true,
      subject: true,
      message: true
    });
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    if (!validateForm()) {
      // Scroll to first error field
      const firstErrorField = document.querySelector('.support-form-input-error, .support-form-textarea-error, select.support-form-input-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (firstErrorField as HTMLElement).focus();
      }
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await supportAPI.createTicket(formData);
      setTicketNumber(response.data.ticket.ticket_number);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setTimeout(() => {
        navigate('/');
      }, 5000);
    } catch (err: any) {
      console.error('Error submitting support ticket:', err);
      const errorMsg = err.response?.data?.error || 'Failed to submit support request. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getBotResponse = (message: string): string => {
    const lowerMsg = message.toLowerCase();
    
    if (lowerMsg.includes('hello') || lowerMsg.includes('hi') || lowerMsg.includes('hey')) {
      return "Hello! How can I assist you today? 😊";
    } else if (lowerMsg.includes('service') || lowerMsg.includes('services')) {
      return "We offer Web Development, Mobile App Development, Software Integration, IT Consultancy, Training Programs, and Cloud Services. Which one interests you?";
    } else if (lowerMsg.includes('price') || lowerMsg.includes('cost') || lowerMsg.includes('how much')) {
      return "Our pricing varies based on project requirements. Could you please fill out the contact form, and our team will provide you with a customized quote?";
    } else if (lowerMsg.includes('timeline') || lowerMsg.includes('how long')) {
      return "Project timelines depend on complexity. Simple websites take 2-4 weeks, while complex applications can take 3-6 months. Would you like to discuss your specific project?";
    } else if (lowerMsg.includes('technology') || lowerMsg.includes('tech stack')) {
      return "We specialize in React, Angular, Vue.js, Django, Node.js, Python, Java, and many more. What technology are you interested in?";
    } else if (lowerMsg.includes('support') || lowerMsg.includes('help')) {
      return "I'm here to help! You can ask me about our services, pricing, technologies, or you can fill out the support form for detailed assistance.";
    } else if (lowerMsg.includes('thanks') || lowerMsg.includes('thank you')) {
      return "You're welcome! Is there anything else I can help you with?";
    } else if (lowerMsg.includes('bye') || lowerMsg.includes('goodbye')) {
      return "Thank you for chatting with us! Feel free to come back if you have more questions. Have a great day! 👋";
    } else {
      return "Thank you for your message. Our support team will get back to you shortly. You can also fill out the support form for detailed assistance.";
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };
    setChatMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setBotThinking(true);
    
    setTimeout(() => {
      const botResponse = getBotResponse(inputMessage);
      const botMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, botMessage]);
      setBotThinking(false);
    }, 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startChat = () => {
    setChatStarted(true);
    setIsChatOpen(true);
  };

  const faqs = [
    {
      question: 'How do I get started with ZiTech Solutions?',
      answer: 'Simply contact us through our contact form or call us directly. We\'ll schedule a consultation to understand your needs and provide a customized solution.'
    },
    {
      question: 'What is your typical project timeline?',
      answer: 'Project timelines vary based on complexity and requirements. A simple website may take 2-4 weeks, while complex applications can take 3-6 months.'
    },
    {
      question: 'Do you provide ongoing support?',
      answer: 'Yes, we offer maintenance and support packages to ensure your systems run smoothly after deployment.'
    },
    {
      question: 'What technologies do you specialize in?',
      answer: 'We specialize in React, Angular, Vue.js, Django, Node.js, Python, Java, and many more modern technologies.'
    }
  ];

  // SVG Icons
  const Icons = {
    Chat: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7443C15.6251 19.5894 14.0782 20.0573 12.5 20.1C11.1801 20.1034 9.87812 19.7951 8.7 19.2L3 21L4.8 15.3C4.2049 14.1219 3.89661 12.8199 3.9 11.5C3.94269 9.9218 4.4106 8.37486 5.25568 7.03248C6.10075 5.6901 7.28815 4.60554 8.7 3.9C9.87812 3.3049 11.1801 2.99661 12.5 3H13C15.0843 3.11499 17.053 3.99476 18.5291 5.47087C20.0052 6.94699 20.885 8.91568 21 11V11.5Z"/>
      </svg>
    ),
    User: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    Email: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    Subject: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"/>
        <path d="M14 2V8H20"/>
      </svg>
    ),
    Message: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
      </svg>
    ),
    Send: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
      </svg>
    ),
    Close: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    ),
    Check: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17L4 12"/>
      </svg>
    ),
    Error: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <circle cx="12" cy="16" r="0.5" fill="currentColor"/>
      </svg>
    ),
    Spinner: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
      </svg>
    ),
    Warning: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 9v4M12 17h.01"/>
        <circle cx="12" cy="12" r="10"/>
      </svg>
    ),
  };

  return (
    <>
      <Navbar />
      <div className="support-page">
        <div className="support-container">
          <div className="support-header">
            <div className="support-header-badge">Get Help</div>
            <h1 className="support-title">Support Center</h1>
            <p className="support-subtitle">
              Get help with your questions or issues. Our team is here to assist you 24/7.
            </p>
          </div>

          {/* Chat Button */}
          {!chatStarted && (
            <button className="support-chat-launch" onClick={startChat}>
              <Icons.Chat />
              Chat with us
            </button>
          )}

          <div className="support-grid">
            {/* FAQ Section */}
            <div className="support-faq-section">
              <h2 className="support-section-title">Frequently Asked Questions</h2>
              <div className="support-faq-list">
                {faqs.map((faq, index) => (
                  <div key={index} className="support-faq-item">
                    <h3 className="support-faq-question">{faq.question}</h3>
                    <p className="support-faq-answer">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form Section */}
            <div className="support-contact-section">
              <h2 className="support-section-title">Contact Support</h2>
              
              {error && (
                <div className="support-error">
                  <Icons.Error />
                  {error}
                </div>
              )}
              
              {submitted ? (
                <div className="support-success">
                  <div className="support-success-icon">
                    <Icons.Check />
                  </div>
                  <h3>Request Sent Successfully!</h3>
                  <p>Your support ticket has been created.</p>
                  <p className="support-ticket-number">
                    Ticket Number: <strong>{ticketNumber}</strong>
                  </p>
                  <p>Our support team will contact you shortly via email.</p>
                  <button className="support-home-btn" onClick={() => navigate('/')}>
                    Return to Home
                  </button>
                </div>
              ) : (
                <form className="support-form" onSubmit={handleSubmit} noValidate>
                  <div className="support-form-group">
                    <label className="support-form-label">
                      <Icons.User />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      className={`support-form-input ${touched.name && formErrors.name ? 'support-form-input-error' : ''}`}
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={loading}
                      aria-invalid={!!formErrors.name}
                      aria-describedby="name-error"
                    />
                    {touched.name && formErrors.name && (
                      <div className="support-form-error" id="name-error">
                        <Icons.Warning />
                        {formErrors.name}
                      </div>
                    )}
                  </div>

                  <div className="support-form-group">
                    <label className="support-form-label">
                      <Icons.Email />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      className={`support-form-input ${touched.email && formErrors.email ? 'support-form-input-error' : ''}`}
                      placeholder="Enter your email address"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={loading}
                      aria-invalid={!!formErrors.email}
                      aria-describedby="email-error"
                    />
                    {touched.email && formErrors.email && (
                      <div className="support-form-error" id="email-error">
                        <Icons.Warning />
                        {formErrors.email}
                      </div>
                    )}
                  </div>

                  <div className="support-form-group">
                    <label className="support-form-label">
                      <Icons.Subject />
                      Subject *
                    </label>
                    <select
                      name="subject"
                      className={`support-form-input ${touched.subject && formErrors.subject ? 'support-form-input-error' : ''}`}
                      value={formData.subject}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={loading}
                      aria-invalid={!!formErrors.subject}
                      aria-describedby="subject-error"
                    >
                      <option value="">Select a subject</option>
                      <option value="technical_issue">Technical Issue</option>
                      <option value="billing_question">Billing Question</option>
                      <option value="feature_request">Feature Request</option>
                      <option value="general_inquiry">General Inquiry</option>
                      <option value="account_issue">Account Issue</option>
                      <option value="other">Other</option>
                    </select>
                    {touched.subject && formErrors.subject && (
                      <div className="support-form-error" id="subject-error">
                        <Icons.Warning />
                        {formErrors.subject}
                      </div>
                    )}
                  </div>

                  <div className="support-form-group">
                    <label className="support-form-label">
                      <Icons.Message />
                      Message *
                    </label>
                    <textarea
                      name="message"
                      className={`support-form-textarea ${touched.message && formErrors.message ? 'support-form-textarea-error' : ''}`}
                      rows={5}
                      placeholder="Describe your issue or question in detail..."
                      value={formData.message}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={loading}
                      aria-invalid={!!formErrors.message}
                      aria-describedby="message-error"
                    />
                    <div className="support-form-character-count">
                      {formData.message.length > 0 && (
                        <span className={formData.message.length < 10 ? 'support-char-invalid' : 'support-char-valid'}>
                          {formData.message.length} / 2000 characters
                          {formData.message.length < 10 && formData.message.length > 0 && ' (Minimum 10 characters)'}
                        </span>
                      )}
                    </div>
                    {touched.message && formErrors.message && (
                      <div className="support-form-error" id="message-error">
                        <Icons.Warning />
                        {formErrors.message}
                      </div>
                    )}
                  </div>

                  <button type="submit" className="support-submit-btn" disabled={loading}>
                    {loading ? (
                      <>
                        <Icons.Spinner />
                        Submitting...
                      </>
                    ) : (
                      'Submit Request'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Live Chat Widget */}
      {isChatOpen && (
        <div className="support-chat-widget">
          <div className="support-chat-header">
            <div className="support-chat-header-info">
              <div className="support-chat-avatar">
                <Icons.User />
              </div>
              <div>
                <h3>Support Chat</h3>
                <p>Typically replies in minutes</p>
              </div>
            </div>
            <button className="support-chat-close" onClick={() => setIsChatOpen(false)}>
              <Icons.Close />
            </button>
          </div>
          
          <div className="support-chat-messages">
            {chatMessages.map((msg) => (
              <div key={msg.id} className={`support-chat-message ${msg.sender}`}>
                <div className="support-message-bubble">
                  <p>{msg.text}</p>
                  <span className="support-message-time">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            {botThinking && (
              <div className="support-chat-message bot">
                <div className="support-message-bubble support-typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          
          <div className="support-chat-input-area">
            <input
              type="text"
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              className="support-chat-input"
            />
            <button className="support-chat-send" onClick={handleSendMessage}>
              <Icons.Send />
            </button>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default Support;